import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { isForbiddenError } from '@/api/isForbiddenError.guard.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { ToastConfig } from './Toastify.config';

const isAdminRoute = () => {
  return window.location.pathname.startsWith('/admin');
};

const isCertificationRoute = () => {
  return window.location.pathname.startsWith('/certification');
};

export const useGetQueryClient = (): QueryClient => {
  const { t } = useTranslationWithPrefix('utils.toasts.fetch');

  const handleMinorError = () => {
    if (isAdminRoute()) return;
    toast(t('error'), ToastConfig.error);
  };

  const handleOperationSuccess = () => {
    if (isAdminRoute() || isCertificationRoute()) return;
    toast(t('success'), ToastConfig.success);
  };

  return useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        mutations: {
          retry: false,
        },
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
        },
      },
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableErrorAlert) {
            return;
          }
          handleError(error, handleMinorError);
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          if (mutation?.options?.meta?.disableSuccessAlert) {
            return;
          }
          handleOperationSuccess();
        },
      }),
      queryCache: new QueryCache({
        onError: (error: unknown, query) => {
          if (!query?.options?.meta?.disableErrorAlert) {
            handleError(error, handleMinorError);
          }
        },
        onSuccess: (_data, query) => {
          if (query?.options?.meta?.enableSuccessAlert) {
            handleOperationSuccess();
          }
        },
      }),
    });
  }, []);
};

const handleError = (error: unknown, handleMinorError: (error: string) => void) => {
  if (!error) {
    handleMinorError('Unknown Error');
    return;
  }
  if (typeof error === 'string') {
    handleMinorError(error);
    return;
  }

  if (isForbiddenError(error)) {
    return;
  }

  const axiosError = error as AxiosError;

  handleMinorError(axiosError?.message);
};
